/**
 * Standalone Money Formatting Module
 * Handles currency formatting with various locale options
 */

/**
 * Format money amount with specified format
 * @param {number|string} cents - Amount in cents
 * @param {string} format - Format string (optional, uses theme settings if available)
 * @returns {string} Formatted money string
 */
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || (window.themeVariables?.settings?.moneyFormat || "£{{ amount }}");
  
  function defaultTo(value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  }
  
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    
    if (isNaN(number) || number == null) {
      return 0;
    }
    
    number = (number / 100).toFixed(precision);
    let parts = number.split(".");
    let dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    let centsAmount = parts[1] ? decimal + parts[1] : "";
    
    return dollarsAmount + centsAmount;
  }
  
  let value = "";
  const match = formatString.match(placeholderRegex);
  
  if (!match) {
    return formatWithDelimiters(cents, 2);
  }
  
  switch (match[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }
  
  return formatString.replace(placeholderRegex, value);
}

/**
 * Format money from dollars to formatted string
 * @param {number} dollars - Amount in dollars (e.g., 12.50)
 * @param {string} format - Format string (optional)
 * @returns {string} Formatted money string
 */
function formatMoneyFromDollars(dollars, format = "") {
  const cents = Math.round(dollars * 100);
  return formatMoney(cents, format);
}

// Export functions for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { formatMoney, formatMoneyFromDollars };
}

// Global namespace for direct script inclusion
window.FormatMoney = {
  formatMoney,
  formatMoneyFromDollars
};

// Legacy theme.Currency support
if (typeof window !== 'undefined') {
  window.theme = window.theme || {};
  window.theme.Currency = {
    formatMoney
  };
}